import request from "./request";


/**
 * Service pour la gestion de la progression des élèves
 */
class ProgressionService {
  
  /**
   * Récupère la progression d'un élève spécifique
   * @param {string|number} eleveId - ID de l'élève
   * @param {string|number} periodId - ID de la période
   * @param {string|number} classeId - ID de la classe
   * @returns {Promise} - Promesse contenant les données de progression
   */
  async getProgressionByEleve(eleveId, periodId, classeId) {
    try {
      const response = await request.get(`/progressions/user/${eleveId}/periode/${periodId}/classe/${classeId}`);
      console.log('Progression récupérée avec succès:', response);
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération de la progression:', error);
      throw error;
    }
  }

  /**
   * Récupère la progression pour une matière spécifique d'un élève
   * @param {string|number} eleveId - ID de l'élève
   * @param {string|number} matiereId - ID de la matière
   * @returns {Promise} - Promesse contenant les données de progression pour la matière
   */
  async getProgressionByMatiereEleve(eleveId, matiereId) {
    try {
      const response = await request(`/progressions/user/${eleveId}/matiere/${matiereId}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération de la progression par matière:', error);
      throw error;
    }
  }

  /**
   * Récupère la progression pour un chapitre spécifique
   * @param {string|number} eleveId - ID de l'élève
   * @param {string|number} chapitreId - ID du chapitre
   * @returns {Promise} - Promesse contenant les données de progression pour le chapitre
   */
  async getProgressionByChapitre(eleveId, chapitreId) {
    try {
      const response = await request(`/progressions/user/${eleveId}/chapitre/${chapitreId}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération de la progression par chapitre:', error);
      throw error;
    }
  }

  /**
   * Récupère la progression pour une leçon spécifique
   * @param {string|number} eleveId - ID de l'élève
   * @param {string|number} leconId - ID de la leçon
   * @returns {Promise} - Promesse contenant les données de progression pour la leçon
   */
  async getProgressionByLecon(eleveId, leconId) {
    try {
      const response = await request(`/progressions/user/${eleveId}/lecon/${leconId}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération de la progression par leçon:', error);
      throw error;
    }
  }

  /**
   * Met à jour le statut d'une leçon pour un élève
   * @param {string|number} eleveId - ID de l'élève
   * @param {string|number} leconId - ID de la leçon
   * @param {string} statut - Nouveau statut ('termine', 'en_cours', etc.)
   * @returns {Promise} - Promesse contenant la réponse de la mise à jour
   */
  async updateLeconStatus(eleveId, leconId, statut) {
    try {
      const response = await request(`/progressions/user/${eleveId}/lecon/${leconId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ statut }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut de la leçon:', error);
      throw error;
    }
  }

  /**
   * Met à jour le statut d'un cours pour un élève
   * @param {string|number} eleveId - ID de l'élève
   * @param {string|number} coursId - ID du cours
   * @param {string} statut - Nouveau statut ('termine', 'en_cours', etc.)
   * @returns {Promise} - Promesse contenant la réponse de la mise à jour
   */
  async updateCoursStatus(eleveId, coursId, statut) {
    try {
      const response = await request(`/progressions/user/${eleveId}/cours/${coursId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ statut }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut du cours:', error);
      throw error;
    }
  }

  /**
   * Met à jour le statut d'une évaluation pour un élève
   * @param {string|number} eleveId - ID de l'élève
   * @param {string|number} evaluationId - ID de l'évaluation
   * @param {string} statut - Nouveau statut ('termine', 'en_cours', etc.)
   * @param {number|null} note - Note obtenue (optionnel)
   * @returns {Promise} - Promesse contenant la réponse de la mise à jour
   */
  async updateEvaluationStatus(eleveId, evaluationId, statut, note = null) {
    try {
      const body = { statut };
      if (note !== null) {
        body.note = note;
      }
      
      const response = await request(`/progressions/user/${eleveId}/evaluation/${evaluationId}/status`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut de l\'évaluation:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques globales de progression pour un élève
   * @param {string|number} eleveId - ID de l'élève
   * @returns {Promise} - Promesse contenant les statistiques de progression
   */
  async getProgressionStats(eleveId) {
    try {
      const response = await request(`/progressions/user/${eleveId}/stats`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique de progression pour un élève
   * @param {string|number} eleveId - ID de l'élève
   * @param {string} periode - Période ('semaine', 'mois', 'trimestre', 'annee')
   * @returns {Promise} - Promesse contenant l'historique de progression
   */
  async getProgressionHistory(eleveId, periode = 'mois') {
    try {
      const response = await request(`/progressions/user/${eleveId}/history?periode=${periode}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      throw error;
    }
  }

  /**
   * Récupère la progression de tous les élèves d'une classe
   * @param {string|number} classeId - ID de la classe
   * @returns {Promise} - Promesse contenant la progression de tous les élèves
   */
  async getProgressionByClasse(classeId) {
    try {
      const response = await request(`/progressions/classe/${classeId}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la récupération de la progression de la classe:', error);
      throw error;
    }
  }

  /**
   * Compare la progression de plusieurs élèves
   * @param {Array} eleveIds - Liste des IDs des élèves à comparer
   * @returns {Promise} - Promesse contenant les données de comparaison
   */
  async compareProgressions(eleveIds) {
    try {
      const response = await request('/progressions/compare', {
        method: 'POST',
        body: JSON.stringify({ eleve_ids: eleveIds }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      console.error('Erreur lors de la comparaison des progressions:', error);
      throw error;
    }
  }
}

export const progressionService = new ProgressionService();
export default progressionService;
